import React, {useState, useEffect} from 'react';
import { PageLoading } from '../../../Components/Loading';
import Chart from '../../../Components/Chart';
import { useJobInvoiceChart } from '../../../Data/Reports';
import Section from '../../../Components/Containers/Section';
import Button from '../../../Components/Button/Button';
import { usePreference } from '../../../Data/Preference';
import Modal from '../../../Components/Modal';
import Form, { Actions, Radio } from '../../../Components/Form';
import { selectableDateRanges, selectablePeriods } from '../../../Data/DateRanges';

const defaultOptions = {
    range: 'lastThreeMonths',
    period: 'week',
    metric: 'value',
}

const MainChart = () => {
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [chartOptions, setChartOptions] = usePreference('mainChartOptions', defaultOptions);
    const { chart, isLoading, refresh } = useJobInvoiceChart(true, chartOptions.range, chartOptions.period);


    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartOptions]);

    if (isLoading || chart === null) {
        return <PageLoading />
    }

    return <>
        <Section title="Jobs / Invoices"
            maxH="h-[40vh]"
            button={<Button theme="light" icon="ChartSimple" zeroMargin onClick={() => setOptionsOpen(true)} />}
        >
            <Chart type="Bar" data={getChartData(chart, chartOptions.metric)} options={getChartFormat(chartOptions)} />
        </Section>

        <Modal isOpen={optionsOpen} setIsOpen={setOptionsOpen} title="Chart Options">
            <Form data={chartOptions} onSubmit={setChartOptions} closeModalOnSubmit>
                <Radio name="range" label="Range" options={selectableDateRanges} />
                <Radio name="period" label="Period" options={selectablePeriods} />
                <Radio name="metric" label="Metric" options={[{value: 'count', label: 'Count'}, {value: 'value', label: 'Value'}]} />
                <Actions submit={{label: 'Change'}} />
            </Form>
        </Modal>
    </>

}

const getChartFormat = (chartOptions) => {
    const tickCallback  = (value) => chartOptions.metric === 'value' ? '£ ' + value : value;
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: false,
                text: 'Jobs / Invoices',
            },
            legend: {
                labels: {
                    filter: filterLegend,
                },
                position: 'bottom',
            }
        },
        scales: {
            x: {
                stacked: true,
                min: 0,
            },
            y: {
                stacked: true,
                min: 0,
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    callback: tickCallback
                },
            },
        },
        layout: {
            padding: 0
        }
    };
}

const getChartData = (chart, metric) => {
    const metricField = (value, count) => metric === 'value' ? value : count;

    const data = {
        labels: chart.dates.map(d => d.replace(/^[0-9]{4}-/, '')),
        datasets: [
            {
                label: 'Jobs - Late',
                data: mergeSets(metricField('total', 'count'), [chart.jobs.late], metricField(100, 1)),
                backgroundColor: '#dc3545',
                stack: 'Jobs',
            },
            {
                label: 'Jobs - Scheduled',
                data: mergeSets(metricField('total', 'count'), [chart.jobs.scheduled, chart.jobs.today], metricField(100, 1)),
                backgroundColor: '#28a745',
                stack: 'Jobs',
            },
            {
                label: 'Jobs - Done',
                data: mergeSets(metricField('total', 'count'), [chart.jobs.done], metricField(100, 1)),
                backgroundColor: '#314695',
                stack: 'Jobs',
            },
            {
                label: 'Invoices - Outstanding',
                data: mergeSets(metricField('outstanding', 'count'), [chart.invoices.outstanding], metricField(100, 1)),
                backgroundColor: '#e83e8c',
                stack: 'Invoices',
            },
            {
                label: 'Invoices - Paid',
                data: mergeSets(metricField('paid', 'count'), [chart.invoices.paid], metricField(100, 1)),
                backgroundColor: '#6f42c1',
                stack: 'Invoices',
            }
        ]
    };

    // console.log('chart', chart);
    // console.log('data', data);

    return data;
}

const mergeSets = (key, sets, divide) => {
    const merged = sets.reduce((merged, set) => {
        set.map((d, i) => merged[i] = (merged[i] ?? 0) + (d[key] ?? 0));
        return merged;
    }, []);

    return merged.map(x => x / divide);
}

const filterLegend = (label, data) => {
    const dataset = data.datasets.find(ds => ds.label === label.text);
    return dataset.data.reduce((sum, value) => sum + value, 0) > 0;
}


export default MainChart;
